/* eslint-disable no-case-declarations */
import { Factory } from "@/data/dao/Factory";
import type { IDataSource } from "@/data/dao/service/BaseServiceDao";
import { PlanningDao } from "@/data/dao/service/PlanningDao";
import type { IOrderApplication } from "@/data/model/OrderApplication";
import type { IConfigSchemaRow } from "@/data/model/Shared";

import { translate } from "@/shared/localization/language";

import { type IOptionNameFormatter, Select } from "@/portal/formControls/Select";
import { Switch } from "@/portal/formControls/Switch";
import { TimePicker } from "@/portal/formControls/TimePicker";

export class DymamicFormBuilder {
    private $parent: JQuery;
    private lblClass: string;
    private valClass: string;

    constructor($parent: JQuery, lblClass?: string, valClass?: string) {
        this.$parent = $parent;

        this.lblClass = lblClass === undefined ? "col-xl-3 col-lg-3" : lblClass;
        this.valClass = valClass === undefined ? "col-lg-9 col-xl-6" : valClass;
    }

    public buildBySchema(schema: Array<IConfigSchemaRow>): DymamicFormBuilder {
        this.$parent.empty();

        for (const i in schema) {
            const cfgSchemaRow = schema[i];

            const group = $(
                '<div class="form-group row"><label class="' +
                    this.lblClass +
                    ' col-form-label">' +
                    cfgSchemaRow.name +
                    (cfgSchemaRow.isRequired ? ' <span class="mandatory">*</span>' : "") +
                    "</label></div>"
            ).data("model", cfgSchemaRow);
            const vC = $('<div class="' + this.valClass + '" />').appendTo(group);

            if (cfgSchemaRow.options !== undefined && cfgSchemaRow.options.length !== 0) {
                const sInpt = $('<select name="' + cfgSchemaRow.key + '" />').appendTo(vC);
                for (const i in cfgSchemaRow.options)
                    sInpt.append(
                        '<option value="' +
                            cfgSchemaRow.options[i].key +
                            '">' +
                            cfgSchemaRow.options[i].value +
                            "</option>"
                    );

                const selectCtrl = new Select(sInpt);

                if (cfgSchemaRow.defaultValue !== undefined) selectCtrl.setValue(cfgSchemaRow.defaultValue);

                if (cfgSchemaRow.isDisabled) selectCtrl.setDisabled(true);

                sInpt.data("ctrl", selectCtrl);
            } else if (cfgSchemaRow.optionDataSource !== undefined) {
                let ds: IDataSource<any> | undefined = undefined;
                let customFormatter: IOptionNameFormatter<any> | undefined = undefined;

                switch (cfgSchemaRow.optionDataSource) {
                    case "UseCase":
                        ds = Factory.getInstance<PlanningDao>(PlanningDao).useCaseDataSource;
                        customFormatter = (e: IOrderApplication) => translate(e.name);
                        break;
                    case "PayUseCase":
                        ds = Factory.getInstance<PlanningDao>(PlanningDao).payUseCaseDataSource;
                        customFormatter = (e: IOrderApplication) => translate(e.name);
                        break;
                }

                if (ds === undefined) console.error("Unkown data source " + cfgSchemaRow.optionDataSource);

                const sInpt = $('<select name="' + cfgSchemaRow.key + '" />').appendTo(vC);
                const selectCtrl = new Select(sInpt, ds, customFormatter);

                if (cfgSchemaRow.isDisabled) selectCtrl.setDisabled(true);

                sInpt.data("ctrl", selectCtrl);
            } else {
                switch (cfgSchemaRow.dataType) {
                    case "String":
                        const tInpt = $(
                            '<input type="text" class="form-control" name="' +
                                cfgSchemaRow.key +
                                '" placeholder="' +
                                (cfgSchemaRow.placeHolder === undefined ? "" : cfgSchemaRow.placeHolder) +
                                '" value="" />'
                        ).appendTo(vC);

                        if (cfgSchemaRow.defaultValue !== undefined) tInpt.val(cfgSchemaRow.defaultValue);

                        if (cfgSchemaRow.isDisabled) tInpt.prop("disabled", true);

                        break;
                    case "Boolean":
                        const bInpt = $(
                            '<input data-switch="true" name="' + cfgSchemaRow.key + '" type="checkbox" />'
                        ).appendTo(vC);
                        const switchCtrl = new Switch(bInpt).renderTo(vC);

                        if (cfgSchemaRow.defaultValue !== undefined) switchCtrl.setValue(cfgSchemaRow.defaultValue);

                        if (cfgSchemaRow.isDisabled) switchCtrl.setDisabled(true);

                        bInpt.data("ctrl", switchCtrl);
                        break;
                    case "Int32":
                        const nInpt = $(
                            '<input type="text" class="form-control" name="' +
                                cfgSchemaRow.key +
                                '" placeholder="' +
                                (cfgSchemaRow.placeHolder === undefined ? "" : cfgSchemaRow.placeHolder) +
                                '" value="" />'
                        ).appendTo(vC);

                        if (cfgSchemaRow.defaultValue !== undefined) nInpt.val(cfgSchemaRow.defaultValue);

                        if (cfgSchemaRow.isDisabled) nInpt.prop("disabled", true);

                        break;
                    case "TimeSpan":
                        const innerGroup = $(
                            '<div class="input-group timepicker"><div class="input-group-append"><span class="input-group-text"><i class="la la-clock-o"></i></span></div>'
                        ).appendTo(vC);
                        const inpt = $(
                            '<input class="form-control" name="' +
                                cfgSchemaRow.key +
                                '"  placeholder="' +
                                (cfgSchemaRow.placeHolder === undefined ? "" : cfgSchemaRow.placeHolder) +
                                '" type="text">'
                        ).prependTo(innerGroup);
                        const inptCtrl = new TimePicker(inpt);

                        if (cfgSchemaRow.defaultValue !== undefined) inptCtrl.setValue(cfgSchemaRow.defaultValue);

                        if (cfgSchemaRow.isDisabled) inptCtrl.setDisabled(true);

                        inpt.data("ctrl", inptCtrl);

                        break;
                }
            }

            if (cfgSchemaRow.description !== undefined)
                vC.append('<span class="form-text text-muted">' + cfgSchemaRow.description + "</span>");

            if (cfgSchemaRow.unavailableMsg !== undefined)
                vC.append('<span class="form-text unavailable">' + cfgSchemaRow.unavailableMsg + "</span>");

            group.appendTo(this.$parent);
        }

        return this;
    }

    public setValues(valueObject?: any): DymamicFormBuilder {
        this.$parent.find("> .row").each((i, e) => {
            const row = $(e);
            const c: IConfigSchemaRow = row.data("model");

            const value = valueObject === undefined ? undefined : valueObject[c.key];

            if ((c.options !== undefined && c.options.length !== 0) || c.optionDataSource !== undefined) {
                const selectCtr: Select<any> = row.find('select[name="' + c.key + '"]').data("ctrl");
                if (value !== undefined) selectCtr.setValue(value);
            } else {
                switch (c.dataType) {
                    case "String":
                        row.find('input[name="' + c.key + '"]').val(value === undefined ? "" : value);

                        break;
                    case "Int32":
                        row.find('input[name="' + c.key + '"]').val(value === undefined ? "" : value);
                        break;
                    case "Boolean":
                        const switchCtrl: Switch = row.find('input[name="' + c.key + '"]').data("ctrl");
                        if (value !== undefined) switchCtrl.setValue(value);

                        break;
                    case "TimeSpan":
                        const timePicker: TimePicker = row.find('input[name="' + c.key + '"]').data("ctrl");
                        timePicker.setValue(value);

                        break;
                }
            }
        });

        return this;
    }

    public validateAndGet(): { hasError: false; values: object } {
        let hasError = false;
        const data = {};

        this.$parent.find("> .row").each((i, e) => {
            const row = $(e);
            const c: IConfigSchemaRow = row.data("model");

            if ((c.options !== undefined && c.options.length !== 0) || c.optionDataSource !== undefined) {
                const selectCtrl: Select<any> = row.find('select[name="' + c.key + '"]').data("ctrl");
                const strVal = selectCtrl.getValue();
                selectCtrl.setError(false);

                if (strVal === undefined) {
                    if (c.isRequired) {
                        hasError = true;
                        selectCtrl.setError(true);
                    }
                } else {
                    data[c.key] = strVal;
                }
            } else {
                switch (c.dataType) {
                    case "String":
                        const strInputField = row.find('input[name="' + c.key + '"]');
                        const strVal = strInputField.val().length === 0 ? undefined : strInputField.val();
                        if (strVal === undefined) {
                            if (c.isRequired) {
                                hasError = true;
                                strInputField.addClass("is-invalid");
                            }
                        } else data[c.key] = strVal;

                        break;
                    case "Int32":
                        const intInputField = row.find('input[name="' + c.key + '"]');
                        const intVal = intInputField.val().length === 0 ? undefined : parseInt(intInputField.val());
                        if (intVal === undefined || isNaN(intVal)) {
                            if (c.isRequired || isNaN(intVal!)) {
                                hasError = true;
                                intInputField.addClass("is-invalid");
                            }
                        } else data[c.key] = intVal;

                        break;
                    case "Boolean":
                        const switchCtrl: Switch = row.find('input[name="' + c.key + '"]').data("ctrl");
                        data[c.key] = switchCtrl.getValue();

                        break;
                    case "TimeSpan":
                        const timePicker: TimePicker = row.find('input[name="' + c.key + '"]').data("ctrl");
                        if (timePicker.getSeconds() === undefined || timePicker.getSeconds() === 0) {
                            if (c.isRequired) {
                                hasError = true;
                                timePicker.setError(true);
                            }
                        } else data[c.key] = timePicker.getValue();

                        break;
                }
            }
        });

        return { hasError: hasError, values: data };
    }
}
